import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, getDocs, collection, query, where, limit, onSnapshot } from "firebase/firestore";
import { toast } from "../hooks/useToast";
import { SitePublicoLoja } from "./SitePublicoLoja";
import { StoreAccount } from "./StoreAccount";
import { LoginCliente } from "./LoginCliente";
import { ContatoLoja } from "./ContatoLoja";
import { ChatLojaDialog, useChatLojaNaoLido } from "./ChatLoja";
import { consumirRetornoGoogle, erroDoRedirectGoogle, atualizarPerfilCliente, clienteAuth } from "../utils/clienteAuth";
import { ativarAppCheck } from "../utils/appCheck";
import { useClienteSession } from "../hooks/useClienteSession";
import { SHARED_SLUGS } from "../utils/lojaPublica";
import { CalcIcon } from "./calculadora/CalcIcon";
import "./storefront.css";

export function SitePublico() {
  const { vendedorId } = useParams(); // pode ser slug (ex: "matriz3d") ou uid
  const [lojaUid, setLojaUid] = useState(null);
  const [vendedor, setVendedor] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [menuAberto, setMenuAberto] = useState(false);
  const [mostrarLogin, setMostrarLogin] = useState(false);
  const [googleReturnUser, setGoogleReturnUser] = useState(null); // usuário Google recém-voltado do redirect, aguardando completar cadastro
  const [tentativa, setTentativa] = useState(0); // incrementar re-dispara o carregamento
  const [linkInvalido, setLinkInvalido] = useState(false); // true só quando o vendedor realmente não existe
  const [chatAberto, setChatAberto] = useState(false);
  // App Check na loja pública: as funções da loja (cupom, pedido, pagamento,
  // login de cliente) passam a receber o token do reCAPTCHA Enterprise
  useEffect(() => { ativarAppCheck(clienteAuth.app); }, []);
  const [chatPendente, setChatPendente] = useState(false); // pediu o chat sem estar logado → abre assim que entrar

  const session = useClienteSession(lojaUid);
  const clienteLogado = session.cliente;
  const authLoading = session.loading;
  const chatNaoLido = useChatLojaNaoLido(lojaUid, clienteLogado?.authUid);

  // Compatibilidade com um redirecionamento iniciado na versão anterior.
  useEffect(() => {
    if (!lojaUid) return;
    const retornou = consumirRetornoGoogle(lojaUid);
    erroDoRedirectGoogle().then(e => { if(e) toast("Não foi possível concluir a entrada com Google. Tente novamente.", "error"); });
    if(retornou) setMostrarLogin(true);
  },[lojaUid]);

  const pedirLogin = () => {
    if(session.error) { session.retry(); return; }
    setGoogleReturnUser(session.semPerfil);
    setMostrarLogin(true);
  };

  const aoLogar = clienteData => {
    session.confirmarLogin(clienteData);
    setMostrarLogin(false);
    setGoogleReturnUser(null);
    if(chatPendente) { setChatPendente(false); setChatAberto(true); }
    toast(`Bem-vindo, ${clienteData.nome?.split(" ")[0] || ""}! ✓`, "success");
  };

  const abrirChat = () => {
    if(clienteLogado) { setMenuAberto(false); setChatAberto(true); }
    else { setChatPendente(true); pedirLogin(); }
  };

  // Carregar vendedor e produtos (produtos em TEMPO REAL)
  useEffect(() => {
    if (!vendedorId) return;
    let unsubProdutos = null;
    let unsubCategorias = null;

    let cancelado = false;

    // getDoc com algumas tentativas — celular costuma falhar a 1ª leitura por rede instável
    const getDocComRetry = async (ref, tentativas = 3) => {
      let ultimoErro;
      for (let i = 0; i < tentativas; i++) {
        try {
          return await getDoc(ref);
        } catch (e) {
          ultimoErro = e;
          await new Promise(r => setTimeout(r, 600 * (i + 1)));
        }
      }
      throw ultimoErro;
    };

    const carregar = async () => {
      setLoading(true);
      setLojaUid(null);
      setMostrarLogin(false);
      setMenuAberto(false);
      setChatAberto(false);
      setChatPendente(false);
      setGoogleReturnUser(null);
      setProdutos([]);
      setCategorias([]);
      setErro("");
      setLinkInvalido(false);
      try {
        // A loja pública lê o documento PÚBLICO `lojas/{slug}` (perfil + PIX).
        // NUNCA lê `users/{uid}` — esse é privado (token MP) e seria negado para visitantes.
        let lojaSnap = await getDocComRetry(doc(db, "lojas", vendedorId));

        // Fallback: acesso pelo uid em vez do slug -> procura a loja por uid
        if ((!lojaSnap.exists() || !lojaSnap.data().uid)) {
          const q = query(collection(db, "lojas"), where("uid", "==", vendedorId), limit(1));
          const res = await getDocs(q);
          if (!res.empty) lojaSnap = res.docs[0];
        }

        if (cancelado) return;
        if (!lojaSnap.exists() || !lojaSnap.data().uid) {
          // Aqui sim o link é realmente inválido (loja não existe)
          setErro("Loja não encontrada");
          setLinkInvalido(true);
          setLoading(false);
          return;
        }

        const loja = lojaSnap.data();
        const uidReal = loja.uid;
        setLojaUid(uidReal);
        setVendedor({
          id: uidReal,
          empresa: loja.empresa || "",
          nome: loja.nome || "",
          logoUrl: loja.logoUrl || "",
          // logoSiteUrl: opcional, cadastrada na aba Loja Online pra quem
          // quer uma logo diferente só na vitrine — o fallback pra logo da
          // empresa acontece no render (abaixo).
          logoSiteUrl: loja.logoSiteUrl || "",
          paymentConfig: loja.paymentConfig || {},
          // WhatsApp/Instagram cadastrados em Configurações → Loja
          contato: loja.contato || {},
        });

        // Vendedores com dados compartilhados guardam produtos na raiz.
        // Reconhece pela flag OU pelo slug conhecido (doc ainda não migrado).
        const naRaiz = loja.produtosNaRaiz === true || SHARED_SLUGS.includes(lojaSnap.id);
        const produtosPath = naRaiz ? "produtos" : `users/${uidReal}/produtos`;

        // Produtos em tempo real (atualiza sozinho, sem recarregar a página)
        const unsub = onSnapshot(collection(db, produtosPath), (snap) => {
          const prods = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(p => p.ativo !== false);
          setProdutos(prods);
          setLoading(false);
        }, (e) => {
          console.error("Erro ao escutar produtos:", e);
          setLoading(false);
        });
        // Se o efeito já foi limpo enquanto carregava (troca de loja), desinscreve já
        if (cancelado) { unsub(); return; }
        unsubProdutos = unsub;

        // Categorias gerenciadas (mesmo caminho dos produtos), em tempo real e na ordem de criação
        const catPath = naRaiz ? "categorias" : `users/${uidReal}/categorias`;
        const unsubCat = onSnapshot(collection(db, catPath), (snap) => {
          const cats = snap.docs
            .map(d => ({ nome: d.data().nome, created_at: d.data().created_at }))
            .filter(c => c.nome)
            .sort((a, b) => (a.created_at?.seconds || 0) - (b.created_at?.seconds || 0))
            .map(c => c.nome);
          setCategorias(cats);
        }, (e) => console.error("Erro ao escutar categorias:", e));
        if (cancelado) { unsubCat(); return; }
        unsubCategorias = unsubCat;
      } catch (e) {
        if (cancelado) return;
        console.error("Erro ao carregar:", e);
        // Falha de rede/leitura — NÃO é link inválido, é só conexão
        setErro("Erro ao carregar dados. Verifique sua conexão.");
        setLinkInvalido(false);
        toast("Erro ao carregar dados", "error");
        setLoading(false);
      }
    };

    carregar();
    return () => { cancelado = true; if (unsubProdutos) unsubProdutos(); if (unsubCategorias) unsubCategorias(); };
  }, [vendedorId, tentativa]);

  // Minha conta → Editar: grava nome/celular/Instagram e atualiza a sessão.
  const salvarPerfil = async dados => {
    const perfil = await atualizarPerfilCliente(lojaUid, clienteLogado, dados);
    session.confirmarLogin(perfil);
  };

  // Fazer logout
  const handleLogout = async () => {
    try {
      await session.sair();
      setMenuAberto(false);
      setChatAberto(false);
      toast("Desconectado", "info");
    } catch { toast("Não foi possível sair. Confira sua conexão e tente novamente.","error"); }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#0a1628",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#00d4aa",
        fontSize: "18px"
      }}>
        Carregando...
      </div>
    );
  }

  if (erro || !vendedor) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#0a1628",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#ef4444",
        padding: "20px",
        textAlign: "center"
      }}>
        <h1 style={{ fontSize: "24px", marginBottom: "10px" }}>⚠️ {erro || "Erro ao carregar"}</h1>
        <p style={{ color: "#94a3b8", marginBottom: 20 }}>
          {linkInvalido
            ? "Este link pode estar inválido ou expirado."
            : "Não foi possível conectar. Verifique sua internet e tente novamente."}
        </p>
        {!linkInvalido && (
          <button
            onClick={() => setTentativa(t => t + 1)}
            style={{ background: "#00d4aa", color: "#06281f", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            🔄 Tentar novamente
          </button>
        )}
      </div>
    );
  }

  // Nome da vitrine: nome da empresa. Logo: usa a específica do site (aba
  // Loja Online) se o vendedor cadastrou uma, senão cai pra logo da empresa.
  const nomePublico = vendedor.empresa || vendedor.nome || "Nossa Loja";
  const logoPublico = vendedor.logoSiteUrl || vendedor.logoUrl || "";

  return (
    <div className="storefront">
      <header className="store-header">
        <div className="store-brand">{logoPublico ? <img src={logoPublico} alt="" /> : <CalcIcon name="box" size={30} />}<strong>{nomePublico}</strong></div>
        <button type="button" className="store-account" disabled={authLoading} onClick={() => { if (clienteLogado) { setMenuAberto(true); } else pedirLogin(); }}>
          <CalcIcon name="user" size={17} />{authLoading ? "Conectando…" : clienteLogado ? "Minha conta" : session.error ? "Reconectar" : session.semPerfil ? "Completar perfil" : "Entrar"}
        </button>
      </header>
      <section className="store-hero" aria-labelledby="store-title">
        <div className="store-hero-copy">
          <span className="store-eyebrow">IDEIAS QUE GANHAM FORMA</span>
          <h1 id="store-title">{nomePublico}</h1>
          <p>Encontre sua próxima peça favorita.<br />Explore nossa coleção de impressões 3D.</p>
          <div className="store-hero-tags"><span>Pronta entrega e encomendas</span><span>Fale com a loja pelo pedido</span></div>
          <ContatoLoja contato={vendedor.contato} nomeLoja={nomePublico} onChat={abrirChat} chatNaoLido={chatNaoLido} />
        </div>
        {logoPublico && <img className="store-hero-logo" src={logoPublico} alt="" />}
      </section>

      {menuAberto && clienteLogado && <StoreAccount cliente={clienteLogado} vendedor={vendedor} onClose={() => setMenuAberto(false)} onLogout={handleLogout} onChat={abrirChat} onSalvarPerfil={salvarPerfil} />}

      {/* Conteúdo */}
      <main className="store-content">
        {session.error && <div className="store-session-notice" role="status"><span>{session.error}</span><button type="button" onClick={session.retry}>Tentar novamente</button></div>}
        <SitePublicoLoja
          key={lojaUid}
          produtos={produtos}
          categorias={categorias}
          vendedor={vendedor}
          cliente={clienteLogado}
          authLoading={authLoading}
          onPedirLogin={pedirLogin}
        />
      </main>
      <footer className="store-footer">
        <ContatoLoja variante="rodape" contato={vendedor.contato} nomeLoja={nomePublico} />
        {nomePublico} · Impressão 3D<br />Prazos e entrega são combinados com a loja.
      </footer>

      {/* Tela de login / cadastro */}
      {mostrarLogin && (
        <LoginCliente
          vendedorId={lojaUid}
          vendedor={vendedor}
          onLogin={aoLogar}
          onClose={() => { setMostrarLogin(false); setGoogleReturnUser(null); setChatPendente(false); }}
          googleUserInicial={googleReturnUser || session.semPerfil}
          confirmarInicial={!!session.pendenteConfirmacao}
        />
      )}

      {/* Chat com o vendedor */}
      {chatAberto && clienteLogado && (
        <ChatLojaDialog vendedorId={lojaUid} nomeLoja={nomePublico} cliente={clienteLogado} naoLido={chatNaoLido} onClose={() => setChatAberto(false)} />
      )}
    </div>
  );
}
